import { create } from 'zustand'

export type Role = 'admin' | 'seller' | 'supply'

export const ROLE_OPTIONS: { value: Role; label: string; persona: string }[] = [
  { value: 'admin', label: 'Admin', persona: 'Volkan (A1 Platform Owner)' },
  { value: 'seller', label: 'Seller', persona: 'Sarah Johnson (BO2 Reseller)' },
  { value: 'supply', label: 'Supply', persona: 'Aybars Ahşap (SO3 Vendor)' },
]

// M1 multi-persona supply shell (B-6 identity-separation operationally simulated):
// Supply role contains 3 distinct personas; each is a separate account in PEXIFY
// (per D-3 buy/supply split + B-6 same-human-different-account invariant).
//
// Per refinement 2026-05-21: persona switcher is FE state (no real auth M0-M2;
// M3 real BE resolves from JWT membership). Mock role switcher state-based.

export type SupplyPersona =
  | 'so3_aybars'
  | 'so2_aybars'
  | 'so2_mehmet'
  | 'so2_hakan'
  // T1.10 shipping-v1 2026-06-05: Emin NJ depot warehouse_operator persona
  // (A-32 §A7 — V2 amendment). A2 internal staff at PEXIFY NJ depot;
  // exposed via supply shell persona switcher (operational simulation —
  // shell route work deferred to Wave-16 backend resume).
  | 'wh_emin'

export type SupplyPersonaInfo = {
  id: SupplyPersona
  org_id: string
  org_name: string
  org_type: 'SO2' | 'SO3' | 'A2'
  description: string
}

export const SUPPLY_PERSONAS: Record<SupplyPersona, SupplyPersonaInfo> = {
  so3_aybars: {
    id: 'so3_aybars',
    org_id: 'so3_aybars',
    org_name: 'Aybars Ahşap',
    org_type: 'SO3',
    description: 'SO3 Vendor — hammadde/yarı mamul tedarikçisi',
  },
  so2_aybars: {
    id: 'so2_aybars',
    org_id: 'so2_aybars',
    org_name: 'Aybars Ahşap (NJ in-house)',
    org_type: 'SO2',
    // B-6 identity-separation: same human, different sides = different
    // accounts. Aybars SO3 = vendor stock supply; Aybars SO2 = NJ in-house
    // production + receive scan team (Phase 4 OM wo_2026_12848_b path).
    description: 'SO2 Production Partner — NJ in-house production + receive scan',
  },
  so2_mehmet: {
    id: 'so2_mehmet',
    org_id: 'so2_mehmet',
    org_name: 'Mehmet Üretim Ortağı',
    org_type: 'SO2',
    description: 'SO2 Production Partner — üretim atölyesi',
  },
  so2_hakan: {
    id: 'so2_hakan',
    org_id: 'so2_hakan',
    org_name: 'Hakan SO2 Bursa Partner',
    org_type: 'SO2',
    description: 'SO2 Production Partner — Bursa tekstil',
  },
  wh_emin: {
    id: 'wh_emin',
    org_id: 'wh_emin',
    org_name: 'Emin (NJ Depot)',
    org_type: 'A2',
    // T1.10 shipping-v1: NJ depot warehouse operator persona — A-32 §A7
    // warehouse_operator tier. Scan + dispatch + packaging advance.
    description: 'A2 NJ Depot — warehouse operator (scan/dispatch/packaging)',
  },
}

type SupplyPersonaStore = {
  supplyPersona: SupplyPersona
  setSupplyPersona: (p: SupplyPersona) => void
}

export const useSupplyPersonaStore = create<SupplyPersonaStore>((set) => ({
  supplyPersona: 'so3_aybars',
  setSupplyPersona: (supplyPersona) => set({ supplyPersona }),
}))

// NOTE: useRoleStore intentionally removed — role is derived from URL pathname
// in Topbar (single source of truth). Keeping ROLE_OPTIONS for label/persona
// reference (used by Topbar for the dropdown options + role-level persona names).
